import { Subscription } from 'rxjs';
import { ProjectHelper } from 'src/app/core';
import { ColumnType, ListingHelper } from 'src/app/core/utils/listing.helper';

import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { Filter, FilterService } from '@brafton/angular-framework-library';
import { IBaseTask } from '@brafton/common-framework-library';

@Component({
  selector: 'shared-tasks-listing-compact',
  templateUrl: './tasks-listing-compact.component.html',
  styleUrls: ['./tasks-listing-compact.component.scss'],
  host: { class: 'tasks-listing-compact' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SharedTasksListingCompactComponent implements OnInit, OnDestroy, OnChanges {
  @Input() tasks!: any;
  @Input() disableProgressLine = false;
  @Input() showProgress = true;
  @Input() darkMode = false;
  @Input() showColumnHeaders = true;
  @Input() showOrgInTitle = false;
  @Input() expandable!: boolean;
  @Input() showImage?: boolean;
  @Input() groupBy?: 'owner' | 'month' | 'status' | 'organization';
  @Input() defaultFilters?: Filter[];
  @Input() sort!: Filter[];
  @Output() sortChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();
  @HostBinding('class.grouped') get groupingChecker() {
    return this.groupBy !== undefined;
  }

  currentDate = new Date();

  @Input() get showOwner() {
    return this.columnBuilder.isShown('owner');
  }
  set showOwner(value: boolean) {
    this.columnBuilder.setShown('owner', value);
  }

  @Input() get showDate() {
    return this.columnBuilder.isShown('date');
  }
  set showDate(value: boolean) {
    this.columnBuilder.setShown('date', value);
  }

  @Input() get showStatus() {
    return this.columnBuilder.isShown('status');
  }
  set showStatus(value: boolean) {
    this.columnBuilder.setShown('status', value);
  }

  @Input() get showAction() {
    return this.columnBuilder.isShown('actions');
  }
  set showAction(value: boolean) {
    this.columnBuilder.setShown('actions', value);
  }

  @Input() get showUnits() {
    return this.columnBuilder.isShown('units');
  }
  set showUnits(value: boolean) {
    this.columnBuilder.setShown('units', value);
  }

  @Input() get showContentType() {
    return this.columnBuilder.isShown('actions');
  }
  set showContentType(value: boolean) {
    this.columnBuilder.setShown('contentType', value);
  }

  @Input() get showOrganization() {
    return this.columnBuilder.isShown('organization');
  }
  set showOrganization(value: boolean) {
    this.columnBuilder.setShown('organization', value);
  }

  // @Input() get showStatusIcon() {
  //   return this.columnBuilder.isShown('status-icon');
  // }
  // set showStatusIcon(value: boolean) {
  //   this.columnBuilder.setShown('status-icon', value || true);
  // }

  // A placeholder for all subscripton so we can unsubscribe all of them in the ondestroy Methode.
  subscriptions = new Subscription();

  columnBuilder = new ListingHelper();

  constructor(
    private projectHelper: ProjectHelper,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
  ) {
    //this.columnBuilder.addColumn('status-icon', ColumnTypes.IconFixed);
    this.columnBuilder.addDefaultColumn('default');
    this.columnBuilder.addColumn('organization', ColumnTypes.Medium);
    this.columnBuilder.addColumn('contentType', ColumnTypes.Small);
    this.columnBuilder.addColumn('owner', ColumnTypes.Medium);
    this.columnBuilder.addColumn('date', ColumnTypes.Small);
    this.columnBuilder.addColumn('units', ColumnTypes.VerySmall);
    this.columnBuilder.addColumn('status', ColumnTypes.Medium);
    this.columnBuilder.addColumn('actions', ColumnTypes.ActionsFixed);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tasks']) {
      switch (this.groupBy) {
        case 'owner':
          this.tasks.items = this.groupByOwner(this.tasks.items);
          break;
        case 'month':
          this.tasks.items = this.groupByMonth(this.tasks.items);
          break;
        case 'organization':
          this.tasks.items = this.groupByFeed(this.tasks.items);
          break;
        case 'status':
          this.tasks.items = this.groupByStatus(this.tasks.items);
          break;
        default:
          break;
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.filterService.registerFilter('sort', this.defaultFilters).subscribe((r: Filter[]) => {
        this.sort = r;
        this.sortChange.emit(this.sort);
      }),
    );
  }

  getDateFormat(dateToFormat: Date): string {
    if (new Date(this.currentDate).getFullYear() === new Date(dateToFormat).getFullYear()) return 'MMM d';
    return 'MMM d, yyyy';
  }

  // This could become a pipe
  getPlannedCaption(plannedStart: Date, plannedEnd: Date): string {
    const currentYear = new Date().getFullYear(); // get the current year
    const plannedStartDate = new Date(plannedStart); // create a new Date object for the planned start date
    const plannedEndDate = new Date(plannedEnd); // create a new Date object for the planned end date

    const startYear = plannedStartDate.getFullYear(); // get the year of the planned start date
    const endYear = plannedEndDate.getFullYear(); // get the year of the planned end date
    const startMonth = plannedStartDate.toLocaleString('default', { month: 'short' }); // get the short name of the month for the planned start date
    const endMonth = plannedEndDate.toLocaleString('default', { month: 'short' }); // get the short name of the month for the planned end date
    const startDay = plannedStartDate.getDate(); // get the day of the month for the planned start date
    const endDay = plannedEndDate.getDate(); // get the day of the month for the planned end date

    const startYearToShow = startYear !== currentYear ? ` ${startYear}` : ''; // determine whether or not to display the year for the planned start date
    const endYearToShow = endYear !== currentYear ? ` ${endYear}` : ''; // determine whether or not to display the year for the planned end date

    // if the planned start and end dates are in the same year and month
    if (startYear === endYear && startMonth === endMonth) {
      // if the planned start and end dates are the same day
      if (startDay === endDay) {
        return `${startMonth} ${startDay}${startYearToShow}`; // return the formatted string without the end date
      } else {
        return `${startMonth} ${startDay} - ${endDay}${startYearToShow}`; // return the formatted string with the start and end dates
      }
    } else {
      return `${startMonth} ${startDay}${startYearToShow} - ${endMonth} ${endDay} ${endYearToShow}`; // return the formatted string with the start and end dates and years
    }
  }

  groupByOwner(items: IBaseTask[]): IBaseTask[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseTask, index: number) => {
      if (index === 0 || cur.owner.id !== groupsRemoved[index - 1].owner.id) {
        const y = acc.concat([{ group: cur.owner, field: 'owner' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  groupByMonth(items: IBaseTask[]): IBaseTask[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseTask, index: number) => {
      if (
        index === 0 ||
        new Date((cur as any).workEnd).toISOString().substring(0, 7) !==
          new Date((groupsRemoved[index - 1] as any).workEnd).toISOString().substring(0, 7)
      ) {
        const y = acc.concat([{ group: new Date((cur as any).workEnd), field: 'date' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  groupByFeed(items: IBaseTask[]): IBaseTask[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseTask, index: number) => {
      if (index === 0 || (cur as any).feedDetails.feedId !== (groupsRemoved[index - 1] as any).feedDetails.feedId) {
        const y = acc.concat([{ group: (cur as any).feedDetails, field: 'organization' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  groupByStatus(items: IBaseTask[]): IBaseTask[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseTask, index: number) => {
      if (index === 0 || cur.status.id !== groupsRemoved[index - 1].status.id) {
        const y = acc.concat([{ group: cur.status, field: 'status' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  updateSort(sort: Sort) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { sort: sort.direction === '' ? undefined : sort.active + '-' + sort.direction + ':' + sort.active },
      queryParamsHandling: 'merge',
    });
  }

  buildSort(): Sort | undefined {
    if (this.sort?.length > 0) {
      const active = this.sort[0].value.split('-')[0];
      const direction = this.sort[0].value.split('-')[1];
      return { active: active, direction: direction } as Sort;
    }
    return;
  }

  generateUnitsTip(phase: any): string {
    if (phase.units.currency.id == 'CoreSmb') {
      const units = (phase.units.totalUnits / 12) * 15;
      return units.toFixed(2) + ' responsive units';
    }

    return phase.units.totalUnits.toFixed(2) + ' responsive units';
  }

  nonMatchingTaskName(task: any): boolean {
    const phase = this.projectHelper.findPhase(task);
    if (!phase) return false;
    return phase?.name !== task?.name;
  }

  selectDarkMode(task: IBaseTask): boolean {
    if (!this.darkMode) return false;

    if (
      this.projectHelper.taskIsCurrentOwner(task) &&
      (task.status.id === 'BeingWorkedOn' || task.status.id === 'ReadyToWorkOn')
    ) {
      return true;
    }

    return false;
  }
}
class ColumnTypes {
  static readonly VerySmall: ColumnType = new ColumnType(7, 11);
  static readonly Small: ColumnType = new ColumnType(12, 14);
  static readonly Medium: ColumnType = new ColumnType(14, 16); // 15
  static readonly Large: ColumnType = new ColumnType(17, 20);
  static readonly IconFixed: ColumnType = new ColumnType(35, 35, true);
  static readonly ActionsFixed: ColumnType = new ColumnType(80, 80, true);
}

interface Group {
  group: any;
  field: string;
}
